import classNames from 'classnames';
import { BsThreeDotsVertical } from 'react-icons/bs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { RouteComponentProps } from 'react-router-dom';

import history from 'utils/history';
import * as toast from 'utils/toast';
import { getIndexOfSN } from 'utils/table';
import { isObjectEmpty } from 'utils/object';
import { getFormattedDate } from 'utils/date';
import { handleError } from 'utils/errorHandler';
import { parse, stringify } from 'utils/queryParam';
import { interpolate, pluralize } from 'utils/string';
import getCurrentReferencePeriod from 'utils/getCurrentReferencePeriod';

import * as routes from 'constants/routes';
import * as permission from 'constants/permissions';
import { DISPLAY_DATE_FORMAT } from 'constants/date';
import { MINIMUM_PAGE_COUNT, MINIMUM_PAGE_ITEM } from 'constants/page';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_NUMBER,
  EVENTS_REFERENCE_PERIOD,
  EDIT_OR_DELETE_PAST_HOLIDAY,
  EVENTS_REFERENCE_PERIOD_PILL_TABS,
} from 'constants/appConstants';
import { HOLIDAY_DESCRIPTION_LENGTH, HOLIDAY_NAME_LENGTH } from 'constants/string';

import Table from 'components/common/table';
import PopOver from 'components/common/popover';
import PillTab from 'components/common/pillTab';
import Pagination from 'components/common/paginator';
import EditDelete from 'components/common/editDelete';
import TableHeader from 'components/common/table/TableHeader';

import * as holidayService from 'services/holidays';

import { useUserPermissions } from 'hooks/query/useUserInfo';
import useCurrentFiscalYear from 'hooks/useCurrentFiscalYear';
import useTitle from 'hooks/useTitle';

type PermissionMap = Record<string, boolean>;

type HolidayQueryState = {
  referencePeriod: string;
  page?: number;
  size?: number;
};

type HolidayQueryParams = {
  page?: number;
  size?: number;
};

type HolidayMeta = {
  page: number;
  total: number;
};

type HolidayRecord = {
  id: number;
  name: string;
  description: string;
  countries?: string[] | null;
  date: string;
};

type HolidayRow = {
  original: HolidayRecord;
  index: number;
};

type PopperData = {
  styles: Record<string, string | number>;
};

type QueryValue = string | string[] | Record<string, unknown> | Array<string | Record<string, unknown>> | undefined;

const getNumberQueryValue = (value: QueryValue, fallback: number): number => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? fallback : parsedValue;
};

const HolidayList: React.FC<RouteComponentProps> = (props) => {
  useTitle('Holidays');

  const [pageData, setPageData] = useState<HolidayMeta>({ page: DEFAULT_PAGE_NUMBER, total: 0 });
  const [pageCount, setPageCount] = useState(DEFAULT_PAGE_NUMBER);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const permissions = useUserPermissions() as PermissionMap;
  const [holidayList, setHolidayList] = useState<HolidayRecord[]>([]);
  const [columnToSortBy, setColumnToSortBy] = useState('date');
  const [isAscendingOrdered, setIsAscendingOrdered] = useState(true);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [openPopoverId, setOpenPopoverId] = useState<number | null>(null);

  const currentReferencePeriod = getCurrentReferencePeriod(props);
  const initialReferencePeriod =
    typeof currentReferencePeriod === 'string'
      ? currentReferencePeriod
      : EVENTS_REFERENCE_PERIOD.UPCOMING.toLowerCase();

  const queryParams = parse(props.location.search, {
    decoder: (str) => decodeURIComponent(str),
  });

  const [queries, setQueries] = useState<HolidayQueryState>({
    referencePeriod: initialReferencePeriod,
    page: getNumberQueryValue(queryParams.page, DEFAULT_PAGE_NUMBER),
    size: getNumberQueryValue(queryParams.size, DEFAULT_PAGE_SIZE),
  });

  const normalizedQueryParams: HolidayQueryParams = useMemo(
    () => ({
      page: getNumberQueryValue(queryParams.page, DEFAULT_PAGE_NUMBER),
      size: getNumberQueryValue(queryParams.size, DEFAULT_PAGE_SIZE),
    }),
    [queryParams.page, queryParams.size]
  );

  const { page, size, referencePeriod } = queries;

  const currentFiscalYear = useCurrentFiscalYear() as Record<string, string>;

  const updateUrl = useCallback(
    (value: HolidayQueryState) => {
      const queryParam = stringify('', value, { arrayFormat: 'comma', encode: false });
      history.push({
        pathname: props.location.pathname,
        search: queryParam,
      });
    },
    [props.location.pathname]
  );

  const isPastHoliday = (date: string) => {
    const today = getFormattedDate();
    const holidayDate = getFormattedDate(date);

    return holidayDate < today;
  };

  const fetchHolidays = useCallback(async () => {
    try {
      setIsDataLoading(true);

      const nextSize = normalizedQueryParams.size || DEFAULT_PAGE_SIZE;
      const nextPage = normalizedQueryParams.page || DEFAULT_PAGE_NUMBER;

      const filters = {
        ...queries,
        page: nextPage,
        size: nextSize,
      };

      setPageSize(nextSize);

      const { data, meta } = (await holidayService.fetch(filters)) as { data: HolidayRecord[]; meta: HolidayMeta };
      const total = meta.total;

      setPageData(meta);

      const formattedData = data.map(({ description, date, ...rest }) => ({
        ...rest,
        description: description || '-',
        date: getFormattedDate(date, DISPLAY_DATE_FORMAT),
      }));

      setHolidayList(formattedData);
      setPageCount(Math.ceil(total / nextSize));
    } catch (error) {
      handleError(error);
    } finally {
      setIsDataLoading(false);
    }
  }, [normalizedQueryParams.page, normalizedQueryParams.size, queries]);

  const handleEdit = (row: HolidayRow) => {
    history.push(interpolate(routes.UPDATE_HOLIDAY, { id: row.original.id }));
  };

  const handleDelete = async (row: HolidayRow) => {
    try {
      await holidayService.remove(row.original.id);
      toast.success({
        title: 'Success',
        message: 'Holiday deleted successfully.',
      });

      const currentPage = normalizedQueryParams.page || DEFAULT_PAGE_NUMBER;
      const shouldGoToPreviousPage = holidayList.length === MINIMUM_PAGE_ITEM && currentPage > DEFAULT_PAGE_NUMBER;

      if (shouldGoToPreviousPage) {
        setQueries((currentQueries) => ({
          ...currentQueries,
          page: currentPage - 1,
        }));

        return;
      }

      fetchHolidays();
    } catch (err) {
      handleError(err);
    }
  };

  useEffect(() => {
    if (isObjectEmpty(currentFiscalYear)) {
      return;
    }

    const loadHolidays = async () => {
      try {
        setIsDataLoading(true);

        const nextSize = normalizedQueryParams.size || DEFAULT_PAGE_SIZE;
        const nextPage = normalizedQueryParams.page || DEFAULT_PAGE_NUMBER;

        const filters = {
          ...queries,
          page: nextPage,
          size: nextSize,
        };

        setPageSize(nextSize);

        const { data, meta } = (await holidayService.fetch(filters)) as { data: HolidayRecord[]; meta: HolidayMeta };
        const total = meta.total;

        setPageData(meta);

        const formattedData = data.map(({ description, date, ...rest }) => ({
          ...rest,
          description: description || '-',
          date: getFormattedDate(date, DISPLAY_DATE_FORMAT),
        }));

        setHolidayList(formattedData);
        setPageCount(Math.ceil(total / nextSize));
      } catch (error) {
        handleError(error);
      } finally {
        setIsDataLoading(false);
      }
    };

    void loadHolidays();
  }, [currentFiscalYear, normalizedQueryParams.page, normalizedQueryParams.size, queries]);

  const getRowProps = (_state: object, rowInfo: HolidayRow) => {
    return {
      className: 'align-items-center d-flex',
      onClick: (e: React.MouseEvent) => {
        if (isPastHoliday(rowInfo.original.date)) {
          toast.error({
            title: 'Warning',
            message: EDIT_OR_DELETE_PAST_HOLIDAY,
          });
          return;
        }

        if (!permissions[permission.HOLIDAYS.UPDATE]) {
          return;
        }

        const url = interpolate(routes.UPDATE_HOLIDAY, { id: rowInfo.original.id });

        if (e.ctrlKey || e.metaKey) {
          window.open(url);
        } else {
          history.push(url);
        }
      },
    };
  };

  const columns = useMemo(
    () => [
      {
        Header: <TableHeader label="SN." identifier="sn" />,
        accessor: 'sn',
        maxWidth: 56,
        sortable: false,
        filterable: false,
        Cell: (cell: HolidayRow) => {
          const index = getIndexOfSN(cell, pageSize, pageData);
          return <span className="capital-text employees__table-text">{index < 10 ? `0${index}` : index}</span>;
        },
      },
      {
        Header: (
          <TableHeader
            label="Title"
            identifier="name"
            columnToSortBy={columnToSortBy}
            isAscendingOrdered={isAscendingOrdered}
            onClick={() => {
              setIsAscendingOrdered((prevStatus) => columnToSortBy !== 'name' || !prevStatus);
              setColumnToSortBy('name');
            }}
          />
        ),
        accessor: 'name',
        maxWidth: 300,
        Cell: (cell: HolidayRow) => {
          return cell.original.name.length > HOLIDAY_NAME_LENGTH ? (
            <PopOver
              interactive
              html={
                <div className="tooltip-info word-break">
                  <p className="tooltip-info__title">{cell.original.name}</p>
                </div>
              }
            >
              <div className="ellipsis">{cell.original.name}</div>
            </PopOver>
          ) : (
            <div className="ellipsis">{cell.original.name}</div>
          );
        },
      },
      {
        Header: <TableHeader label="Description" identifier="description" />,
        accessor: 'description',
        sortable: false,
        filterable: false,
        maxWidth: 640,
        Cell: (cell: HolidayRow) => {
          return cell.original.description.length > HOLIDAY_DESCRIPTION_LENGTH ? (
            <PopOver
              interactive
              html={
                <div className="tooltip-info word-break">
                  <p className="tooltip-info__title">{cell.original.description}</p>
                </div>
              }
            >
              <div className="ellipsis">{cell.original.description}</div>
            </PopOver>
          ) : (
            <div className="ellipsis">{cell.original.description}</div>
          );
        },
      },
      {
        Header: (
          <TableHeader
            label="Countries"
            identifier="countries"
            columnToSortBy={columnToSortBy}
            isAscendingOrdered={isAscendingOrdered}
            onClick={() => {
              if (columnToSortBy !== 'countries') {
                setIsAscendingOrdered(true);
              } else {
                setIsAscendingOrdered(!isAscendingOrdered);
              }
              setColumnToSortBy('countries');
            }}
          />
        ),
        accessor: 'countries',
        maxWidth: 260,
        Cell: (cell: HolidayRow) => {
          const { countries } = cell.original;
          return <>{countries ? countries.join(', ') : 'All'}</>;
        },
      },
      {
        Header: (
          <TableHeader
            label="Date"
            identifier="date"
            columnToSortBy={columnToSortBy}
            isAscendingOrdered={isAscendingOrdered}
            onClick={() => {
              setIsAscendingOrdered((prevStatus) => columnToSortBy !== 'date' || !prevStatus);
              setColumnToSortBy('date');
            }}
          />
        ),
        accessor: 'date',
        maxWidth: 120,
      },
    ],
    [columnToSortBy, isAscendingOrdered, pageData, pageSize]
  );

  const getEditCell = () => {
    const canEdit = Boolean(permissions[permission.HOLIDAYS.UPDATE]);
    const canDelete = Boolean(permissions[permission.HOLIDAYS.DELETE]);
    const canEditOrDelete = canEdit || canDelete;

    return {
      Header: '',
      width: 128,
      className: 'margin-left-auto',
      Cell: (row: HolidayRow) =>
        !isPastHoliday(row.original.date) && canEditOrDelete ? (
          <span
            className="verticle-ellipse cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <PopOver
              interactive
              open={openPopoverId === row.original.id}
              onRequestClose={() => setOpenPopoverId(null)}
              theme="light"
              size="regular"
              customClass="overflow-menu__regular"
              position="left-start"
              disableOnScroll={true}
              popperOptions={{
                modifiers: {
                  addZIndex: {
                    enabled: true,
                    order: 810,
                    fn: (data: PopperData) => ({
                      ...data,
                      styles: {
                        ...data.styles,
                        zIndex: 10,
                      },
                    }),
                  },
                },
              }}
              html={
                <EditDelete
                  onClose={() => setOpenPopoverId(null)}
                  onEdit={() => handleEdit(row)}
                  onDelete={() => handleDelete(row)}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  type="holiday"
                  title={row.original.name}
                />
              }
            >
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenPopoverId(openPopoverId === row.original.id ? null : row.original.id);
                }}
              >
                <BsThreeDotsVertical size={20} />
              </span>
            </PopOver>
          </span>
        ) : null,
    };
  };

  useEffect(() => {
    updateUrl({
      ...queries,
    });
  }, [referencePeriod, page, size, queries, updateUrl]);

  const onPageChange = (selectedPage: number) => {
    setQueries((value) => ({ ...value, page: selectedPage }));
  };

  const onPageSizeChange = (selectedSize: number) => {
    setQueries((value) => ({ ...value, page: DEFAULT_PAGE_NUMBER, size: selectedSize }));
  };

  const handleApplyFilter = (filters: Pick<HolidayQueryState, 'referencePeriod'>) => {
    setQueries({ ...filters, page: DEFAULT_PAGE_NUMBER, size: DEFAULT_PAGE_SIZE });
  };

  return (
    <div className="full-scope-card">
      <div className="full-scope-card__header table-header name-wrap">
        <div>
          <div className="table-title">Holidays</div>
          <div className="table-subtitle">
            Showing {holidayList.length} {pluralize('Result', holidayList.length)}
          </div>
        </div>
        <div className="table-header__right">
          <PillTab
            tabs={EVENTS_REFERENCE_PERIOD_PILL_TABS}
            onTabClick={(value) => handleApplyFilter({ referencePeriod: String(value) })}
            isActive={(value) => String(value) === queries.referencePeriod.toLowerCase()}
          />
        </div>
      </div>
      <div className={classNames('full-scope-card__content')}>
        <>
          <Table
            loading={isDataLoading}
            data={holidayList}
            columns={[...columns, getEditCell()]}
            showPagination={false}
            noDataText="No holidays to show."
            getTrProps={getRowProps}
          />
          <Pagination
            pageData={pageData}
            pageCount={+(pageCount || MINIMUM_PAGE_COUNT)}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </>
      </div>
    </div>
  );
};

export default HolidayList;
